// careTicks.js — TEST 2.14. A per-encounter, content-addressed completion
// tracker, replacing the flat `<type>CheckedItems` array of POSITION ids
// (`careOrders-0`, `careOrders-1`, …) that InstructionsScreen and DeliverScreen
// both wrote and read. A position id conflates "the item at index 3" across
// every encounter a case will ever have (measured: three different position-id
// schemes existed — `instruction-${idx}`, `${orderType}-${idx}`,
// `${task.type}-${idx}` — none of them naming an ITEM, all of them naming a
// SLOT) and breaks the moment the doctor's order list is reordered or a new
// follow-up encounter adds its own, unrelated orders under the same key.
//
// Its one import (./asText.js, itself import-free, with the extension) loads directly in Node,
// because its gate loads this file directly — the same discipline caseEncounters.js documents.
import { asFirst } from './asText.js'

// ── item identity: content-addressed, not position-addressed ──────────────
function fnv1a32(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16)
}

function normalize(text) {
  return String(text == null ? '' : text).trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * canonicalItemText(item) — the ONE text extraction every caller uses to turn
 * an order item into the string `itemKey` hashes. This is NOT cosmetic: two
 * different extraction rules at the write site (InstructionsScreen, saving a
 * tick) and the read site (DeliverScreen's CareCard, legacyIds) would compute
 * two different keys for the SAME item, and a tick would silently fail to
 * show as ticked anywhere it was not written. So there is exactly one rule,
 * here, and both screens call it (via `keyItems` / `legacyIds`) — never their
 * own copy. Accepts either a raw order item (a string, or an object from
 * ordersPlaced — `item.name || item.device || item.description || item.test`,
 * matching InstructionsScreen's original fallback chain) or an object that
 * already carries a plain-string `.text` (parseInstructions' output, or an
 * already-shaped `{id, text}` item) — either way, the same string comes out.
 */
export function canonicalItemText(item) {
  if (typeof item === 'string') return item
  if (item && typeof item === 'object') {
    if (typeof item.text === 'string') return item.text
    // PROD-1156: the SAME clean text InstructionsScreen draws (asFirst), so a tick on a {label}/{title}/{summary}
    // item is keyed by the text the patient saw and reaches the report. A string/{name,device,...} item keeps its old key.
    return asFirst(item.name, item.device, item.description, item.test, item)
  }
  return String(item == null ? '' : item)
}

/**
 * itemKey(type, text, n) — content-addressed: the same wording in the same
 * order-type bucket always gets the same key, regardless of where it sits in
 * the list. `n` is the count of identical (normalized) texts already seen
 * earlier in the same list, so two items reading "Take ibuprofen" still tick
 * independently (E6) instead of colliding on one key.
 */
export function itemKey(type, text, n) {
  return `${type}:${fnv1a32(normalize(text))}~${n}`
}

/**
 * Assigns a `key` (and a canonical `.text`) to every item in a list, in
 * display order. `items` may be raw order items OR objects that already
 * carry `.text` — see `canonicalItemText`. This is the ONE place `n` (the
 * duplicate-text counter) is computed, so a caller never has to re-derive it.
 * Returns `{...item, text, key}` (an already-shaped item's own `.id` etc.
 * survive the spread; its `.text` is overwritten with the same value it
 * already had).
 */
export function keyItems(type, items) {
  const seen = new Map()
  return items.map((item) => {
    const text = canonicalItemText(item)
    const norm = normalize(text)
    const n = seen.get(norm) || 0
    seen.set(norm, n + 1)
    const base = (item && typeof item === 'object') ? item : {}
    return { ...base, text, key: itemKey(type, text, n) }
  })
}

// ── Q4 (Alex): daily vs one-off, by order TYPE ─────────────────────────────
// Care orders and additional instructions reset every day; tests, DME,
// procedures, referrals and custom orders are one-off — ticked once, stay
// ticked. `additionalOrders` (legacy) and `other` (the freeform "Additional
// Instructions" bucket physicians actually write into today) share that
// exact title in orderTypeConfig, so both count as "additional instructions".
const DAILY_TYPES = new Set(['careOrders', 'additionalOrders', 'other'])
export function isDaily(orderType) {
  return DAILY_TYPES.has(orderType)
}

// ── Q5 (Alex): a tracker day ends at midnight on the PATIENT'S DEVICE ──────
// en-CA is the one built-in Intl locale whose formatted date IS YYYY-MM-DD;
// this is the device's local calendar day, never `toISOString` (UTC — 1dc(l),
// the same trap dateUtils.js already documents for a stored date).
export function dayKey(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

// ── the store shape, and the ONE patch shape that ever writes it ──────────
// Store shape: careTicks = { [enc]: { [key]: {days:{[dayKey]: bool}} | {once: bool} } }
// `enc` is `e${encounter_number}` — see caseEncounters.js's currentEncounterNumber.

/**
 * tickPatch(enc, key, daily, day, on) — the ONE writer. An un-tick is an
 * EXPLICIT `false`, never an omitted key: `update_case`'s deep_merge never
 * removes a key (TEST 2.14 contract, pinned in tools/test_deliver_tick_merge.py),
 * so omitting one would leave a stale `true` in place forever.
 */
export function tickPatch(enc, key, daily, day, on) {
  return { careTicks: { [enc]: { [key]: daily ? { days: { [day]: !!on } } : { once: !!on } } } }
}

/** Is `key` ticked right now, for encounter `enc` on day `day`? */
export function isTicked(store, enc, key, daily, day) {
  const entry = store?.[enc]?.[key]
  if (!entry) return false
  return daily ? !!entry.days?.[day] : !!entry.once
}

/** `{done, total}` over a list of keyed items (from keyItems), for `n/total`. */
export function completion(store, enc, day, keyedItems, daily) {
  let done = 0
  for (const item of keyedItems) if (isTicked(store, enc, item.key, daily, day)) done++
  return { done, total: keyedItems.length }
}

/**
 * legacyIds(type, items, store, enc, day) — the position ids of the items
 * ticked NOW (`${type}-${idx}` — the id scheme InstructionsScreen and
 * DeliverScreen have always used, and `instruction-${idx}` for `other`'s
 * free-text items, matching parseInstructions), for the two readers that
 * still want a flat array: the physician's pre-visit report, and the legacy
 * `<type>CheckedItems` field kept for anything not yet reading `careTicks`.
 * Computed FROM the store, never written independently of it, so the two can
 * never disagree. `items` is the RAW (un-keyed) item list, in display order —
 * the same array `keyItems` would be given.
 */
export function legacyIds(type, items, store, enc, day) {
  const daily = isDaily(type)
  const keyed = keyItems(type, items)
  const out = []
  keyed.forEach((item, idx) => {
    if (isTicked(store, enc, item.key, daily, day)) {
      out.push(type === 'other' ? `instruction-${idx}` : `${type}-${idx}`)
    }
  })
  return out
}
